"use client";

import {
	RiCheckLine,
	RiExpandUpDownLine,
	RiMapPinLine,
} from "@remixicon/react";
import type { LocationGroup } from "@workspace/shared-utils";
import { Button } from "@workspace/ui/components/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@workspace/ui/components/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";

export interface ShowCityFilterProps {
	value: string;
	onChange: (value: string) => void;
	locationGroups: LocationGroup[];
	className?: string;
}

export function ShowCityFilter({
	value,
	onChange,
	locationGroups,
	className,
}: ShowCityFilterProps) {
	const [open, setOpen] = useState(false);

	const getDisplayName = (city: string) => {
		if (!city || city === "all") return "All Cities";
		return city;
	};

	const handleSelect = useCallback(
		(currentValue: string) => {
			const scrollPosition = window.scrollY;
			onChange(currentValue);
			setOpen(false);
			requestAnimationFrame(() => {
				window.scrollTo(0, scrollPosition);
			});
		},
		[onChange],
	);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn("h-10 w-full justify-between px-3", className)}
				>
					<div className="flex min-w-0 items-center gap-2">
						<RiMapPinLine className="h-4 w-4 shrink-0" />
						<span className="truncate">{getDisplayName(value)}</span>
					</div>
					<RiExpandUpDownLine className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[300px] p-0"
				side="bottom"
				align="start"
				sideOffset={4}
				collisionPadding={8}
			>
				<Command>
					<CommandInput placeholder="Search cities..." />
					<CommandList className="max-h-[200px]">
						<CommandEmpty>No cities found.</CommandEmpty>
						<CommandGroup>
							<CommandItem value="all" onSelect={() => handleSelect("all")}>
								<div className="flex items-center justify-between w-full">
									<span className="truncate">All Cities</span>
									<RiCheckLine
										className={cn(
											"ml-2 h-4 w-4 shrink-0",
											value === "all" ? "opacity-100" : "opacity-0",
										)}
									/>
								</div>
							</CommandItem>
							{value !== "all" && (
								<CommandItem
									value="clear-selection"
									onSelect={() => handleSelect("all")}
									className="text-destructive"
								>
									<span>Clear Selection</span>
								</CommandItem>
							)}
							{locationGroups.map((group, index) => (
								<div key={index}>
									{group.type === "metro" && group.metroArea && (
										<>
											<CommandItem
												value={group.metroArea}
												onSelect={() => handleSelect(group.metroArea!)}
											>
												<div className="flex items-center justify-between w-full">
													<span className="truncate">{group.metroArea}</span>
													<RiCheckLine
														className={cn(
															"ml-2 h-4 w-4 shrink-0",
															value === group.metroArea ? "opacity-100" : "opacity-0",
														)}
													/>
												</div>
											</CommandItem>
											{group.cities.map((city) => (
												<CommandItem
													key={city}
													value={city}
													onSelect={() => handleSelect(city)}
													className="pl-4"
												>
													<div className="flex items-center justify-between w-full">
														<span className="truncate">{getDisplayName(city)}</span>
														<RiCheckLine
															className={cn(
																"ml-2 h-4 w-4 shrink-0",
																value === city ? "opacity-100" : "opacity-0",
															)}
														/>
													</div>
												</CommandItem>
											))}
										</>
									)}
									{group.type === "standalone" &&
										group.cities.map((city) => (
											<CommandItem
												key={city}
												value={city}
												onSelect={() => handleSelect(city)}
											>
												<div className="flex items-center justify-between w-full">
													<span className="truncate">{getDisplayName(city)}</span>
													<RiCheckLine
														className={cn(
															"ml-2 h-4 w-4 shrink-0",
															value === city ? "opacity-100" : "opacity-0",
														)}
													/>
												</div>
											</CommandItem>
										))}
								</div>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
